package com.tovtalk.ui.screen.chat

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.Send
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextField
import androidx.compose.material3.TextFieldDefaults
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import com.tovtalk.di.koinInject
import com.tovtalk.di.disposableKoinInject
import com.tovtalk.domain.entity.Message
import com.tovtalk.presentation.state.UiState
import com.tovtalk.presentation.viewmodel.chat.ChatViewModel
import com.tovtalk.ui.components.TovTalkAvatar
import com.tovtalk.ui.theme.TovTalkBrand
import com.tovtalk.ui.components.KashrutRestrictionDialog
import com.tovtalk.ui.theme.TovTalkTheme
import com.tovtalk.utils.strings.StringKeys
import com.tovtalk.utils.strings.StringProvider
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.TextButton
import kotlinx.coroutines.delay
import kotlin.time.Instant

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ChatDetailScreen(
    receiverId: Int,
    receiverName: String,
    onNavigateBack: () -> Unit,
    onNavigateToProfile: ((Int) -> Unit)? = null,
    onNavigateToSettings: (() -> Unit)? = null,
    viewModel: ChatViewModel = disposableKoinInject()
) {
    val messagesState by viewModel.messages.collectAsState()
    val isOtherTyping by viewModel.isOtherTyping.collectAsState()
    val receiverProfile by viewModel.receiverProfile.collectAsState()
    val currentUserId by viewModel.currentUserId.collectAsState()
    val canSendToReceiver by viewModel.canSendToReceiver.collectAsState()
    val sendMessageError by viewModel.sendMessageError.collectAsState()
    val showKashrutDialog by viewModel.showKashrutDialog.collectAsState()
    val kashrutDialogMessage by viewModel.kashrutDialogMessage.collectAsState()
    val showUserDoesNotAcceptMessagesDialog by viewModel.showUserDoesNotAcceptMessagesDialog.collectAsState()
    val currentUserIsReadOnly by viewModel.currentUserIsReadOnly.collectAsState()

    var messageText by remember { mutableStateOf("") }
    val listState = rememberLazyListState()

    LaunchedEffect(receiverId) {
        viewModel.initChat(receiverId)
    }

    DisposableEffect(Unit) {
        onDispose { viewModel.onClear() }
    }

    val isTyping = messageText.isNotBlank()
    LaunchedEffect(isTyping) {
        if (isTyping) {
            viewModel.setTyping(true)
            delay(3000)
            viewModel.setTyping(false)
        } else {
            viewModel.setTyping(false)
        }
    }

    val messageListForScroll = (messagesState as? UiState.Success)?.data ?: emptyList()
    LaunchedEffect(messageListForScroll.size) {
        if (messageListForScroll.isNotEmpty()) {
            listState.animateScrollToItem(messageListForScroll.size - 1)
        }
    }

    if (showKashrutDialog) {
        KashrutRestrictionDialog(
            message = kashrutDialogMessage,
            onDismiss = { viewModel.dismissKashrutDialog() },
            onGoToSettings = if (currentUserIsReadOnly) {
                {
                    viewModel.dismissKashrutDialog()
                    onNavigateToSettings?.invoke()
                }
            } else null
        )
    }

    if (showUserDoesNotAcceptMessagesDialog) {
        AlertDialog(
            onDismissRequest = { viewModel.dismissUserDoesNotAcceptMessagesDialog() },
            title = { Text(StringProvider.get(StringKeys.KASHRUT_DIALOG_TITLE)) },
            text = { Text(StringProvider.get(StringKeys.CHAT_USER_DOES_NOT_ACCEPT_MESSAGES)) },
            confirmButton = {
                TextButton(onClick = { viewModel.dismissUserDoesNotAcceptMessagesDialog() }) {
                    Text(StringProvider.get(StringKeys.KASHRUT_DIALOG_CANCEL))
                }
            }
        )
    }

    Scaffold(
        containerColor = TovTalkTheme.colors.backgroundPrimary,
        topBar = {
            TopAppBar(
                title = {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .clickable(enabled = onNavigateToProfile != null) {
                                onNavigateToProfile?.invoke(receiverId)
                            },
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        TovTalkAvatar(
                            imageUrl = receiverProfile?.profilePhoto,
                            size = TovTalkTheme.sizes.avatarSm
                        )
                        Spacer(modifier = Modifier.width(TovTalkTheme.spacing.xs))
                        Column {
                            Text(
                                receiverName,
                                style = TovTalkTheme.typography.titleMedium,
                                color = TovTalkTheme.colors.textPrimary
                            )
                            if (isOtherTyping) {
                                Text(
                                    StringProvider.get("chat_typing"),
                                    style = TovTalkTheme.typography.labelSmall,
                                    color = TovTalkTheme.colors.brand
                                )
                            }
                        }
                    }
                },
                navigationIcon = {
                    IconButton(onClick = onNavigateBack) {
                        Icon(
                            Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = StringProvider.get("common_back"),
                            tint = TovTalkTheme.colors.textPrimary
                        )
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = TovTalkTheme.colors.backgroundPrimary,
                    titleContentColor = TovTalkTheme.colors.textPrimary,
                    navigationIconContentColor = TovTalkTheme.colors.textPrimary
                )
            )
        },
        bottomBar = {
            Surface(
                tonalElevation = TovTalkTheme.elevation.bottomBar,
                color = TovTalkTheme.colors.surfaceElevated,
                modifier = Modifier.imePadding()
            ) {
                Column {
                    if (!canSendToReceiver) {
                        Text(
                            text = StringProvider.get("chat_cannot_send_view_only"),
                            style = TovTalkTheme.typography.labelSmall,
                            color = TovTalkTheme.colors.error,
                            modifier = Modifier.padding(horizontal = TovTalkTheme.spacing.screenPadding, vertical = TovTalkTheme.spacing.xxs)
                        )
                    }
                    if (sendMessageError != null) {
                        Text(
                            text = sendMessageError!!,
                            style = TovTalkTheme.typography.labelSmall,
                            color = TovTalkTheme.colors.error,
                            modifier = Modifier
                                .padding(horizontal = TovTalkTheme.spacing.screenPadding, vertical = TovTalkTheme.spacing.xxs)
                                .clickable { viewModel.clearSendMessageError() }
                        )
                    }
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(TovTalkTheme.spacing.xs)
                            .navigationBarsPadding(),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        TextField(
                            value = messageText,
                            onValueChange = { messageText = it },
                            modifier = Modifier.weight(1f),
                            placeholder = { Text(StringProvider.get("chat_type_message"), style = TovTalkTheme.typography.bodyMedium) },
                            maxLines = 4,
                            enabled = canSendToReceiver,
                            colors = TextFieldDefaults.colors(
                                focusedIndicatorColor = Color.Transparent,
                                unfocusedIndicatorColor = Color.Transparent,
                                focusedContainerColor = TovTalkTheme.colors.surfaceOverlay,
                                unfocusedContainerColor = TovTalkTheme.colors.surfaceOverlay,
                                cursorColor = TovTalkTheme.colors.brand,
                                focusedTextColor = TovTalkTheme.colors.textPrimary,
                                unfocusedTextColor = TovTalkTheme.colors.textPrimary
                            ),
                            shape = RoundedCornerShape(TovTalkTheme.radius.lg)
                        )
                        Spacer(modifier = Modifier.width(TovTalkTheme.spacing.xs))
                        IconButton(
                            onClick = {
                                if (messageText.isNotBlank() && canSendToReceiver) {
                                    viewModel.sendTextMessage(receiverId, messageText)
                                    messageText = ""
                                }
                            },
                            enabled = messageText.isNotBlank() && canSendToReceiver
                        ) {
                            Icon(
                                Icons.AutoMirrored.Filled.Send,
                                contentDescription = StringProvider.get("common_send"),
                                tint = if (messageText.isNotBlank() && canSendToReceiver) TovTalkTheme.colors.brand else TovTalkTheme.colors.textTertiary
                            )
                        }
                    }
                }
            }
        }
    ) { paddingValues ->
        val messageList = (messagesState as? UiState.Success)?.data ?: emptyList()
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(paddingValues)
        ) {
            when (messagesState) {
                is UiState.Loading -> {
                    CircularProgressIndicator(
                        modifier = Modifier.align(Alignment.Center),
                        color = TovTalkTheme.colors.brand
                    )
                }
                is UiState.Success -> {
                    LazyColumn(
                        state = listState,
                        modifier = Modifier.fillMaxSize(),
                        contentPadding = PaddingValues(TovTalkTheme.spacing.screenPadding),
                        verticalArrangement = Arrangement.spacedBy(TovTalkTheme.spacing.xs)
                    ) {
                        items(
                            items = messageList,
                            key = { it.id }
                        ) { message ->
                            MessageBubble(
                                message = message,
                                isFromMe = when {
                                    currentUserId != null -> message.senderId == currentUserId
                                    else -> message.senderId != receiverId
                                },
                                receiverId = receiverId
                            )
                        }
                    }
                }
                is UiState.Error -> {
                    Text(
                        text = (messagesState as UiState.Error).message,
                        modifier = Modifier.align(Alignment.Center),
                        color = TovTalkTheme.colors.error
                    )
                }
                else -> {}
            }
        }
    }
}

// Bubble colors: distinct for "mine" vs "theirs" (like Android)
private val ChatBubbleMine = TovTalkBrand
// "Theirs" bubble color uses TovTalkTheme.colors.chatBubbleTheirs (dark/light aware)

@Composable
private fun MessageBubble(
    message: Message,
    isFromMe: Boolean,
    receiverId: Int
) {
    val bubbleBg = if (isFromMe) ChatBubbleMine else TovTalkTheme.colors.chatBubbleTheirs
    val bubbleTextColor = if (isFromMe) TovTalkTheme.colors.onBrand else TovTalkTheme.colors.textPrimary

    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = if (isFromMe) Arrangement.End else Arrangement.Start,
        verticalAlignment = Alignment.Bottom
    ) {
        if (isFromMe) Spacer(modifier = Modifier.weight(1f))
        Row(
            verticalAlignment = Alignment.Bottom,
            horizontalArrangement = if (isFromMe) Arrangement.End else Arrangement.Start,
            modifier = Modifier.widthIn(max = 280.dp)
        ) {
            Box(
                modifier = Modifier
                    .heightIn(min = TovTalkTheme.sizes.avatarMd)
                    .clip(
                        RoundedCornerShape(
                            topStart = TovTalkTheme.radius.lg,
                            topEnd = TovTalkTheme.radius.lg,
                            bottomStart = if (isFromMe) TovTalkTheme.radius.lg else TovTalkTheme.radius.xs,
                            bottomEnd = if (isFromMe) TovTalkTheme.radius.xs else TovTalkTheme.radius.lg
                        )
                    )
                    .background(bubbleBg)
                    .padding(horizontal = TovTalkTheme.spacing.sm, vertical = TovTalkTheme.spacing.xs)
            ) {
                Text(
                    text = message.content?.ifBlank { "" } ?: "",
                    color = bubbleTextColor,
                    style = TovTalkTheme.typography.bodyLarge
                )
            }
            if (isFromMe) {
                Spacer(modifier = Modifier.width(TovTalkTheme.spacing.xxs))
                MessageReadStatusIcon(
                    readAt = message.readAt,
                    receivedAt = message.receivedAt
                )
            }
        }
        if (!isFromMe) Spacer(modifier = Modifier.weight(1f))
    }
}

// Read status: 1 tick = sent, 2 ticks = delivered, 2 blue ticks = read. Use Text(✓) so it renders on iOS.
// Tick colors use TovTalkTheme.colors.chatTickRead / chatTickSent

@Composable
private fun MessageReadStatusIcon(
    readAt: Instant?,
    receivedAt: Instant?
) {
    val (tickColor, tickCount) = when {
        readAt != null -> TovTalkTheme.colors.chatTickRead to 2
        receivedAt != null -> TovTalkTheme.colors.chatTickSent to 2
        else -> TovTalkTheme.colors.chatTickSent to 1
    }
    val ticks = "\u2713".repeat(tickCount) // ✓✓ or ✓
    Text(
        text = ticks,
        color = tickColor,
        style = TovTalkTheme.typography.bodyMedium,
        modifier = Modifier.padding(bottom = TovTalkTheme.spacing.xxxs)
    )
}
